
import React, { useState, useMemo } from 'react';
import { 
  ArrowUpRight, ArrowDownLeft, Wallet, Activity, 
  Plus, X, 
  Users, Save,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle,
  Clock
} from 'lucide-react';
import { Card } from './SharedComponents.tsx';
import { Order, UserRole, DriverPayment, DriverPaymentType, OrderStatus, ExpenseStatus } from './types.ts';

import { motion } from 'framer-motion';

const MonitorCard = ({ label, realizado, previsto, pct, icon: Icon, color, barColor, isBalance }: any) => (
  <Card className="p-6 md:p-8 bg-white border-none shadow-xl rounded-[32px] group hover:shadow-2xl transition-all">
    <div className="flex justify-between items-start mb-6">
      <div className={`p-4 rounded-2xl bg-gray-50 ${color} group-hover:scale-110 transition-transform`}>
        <Icon size={24} strokeWidth={2.5} />
      </div>
      <div className="text-right">
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{label}</p>
        <h4 className={`text-2xl font-black italic tracking-tighter ${color}`}>
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(realizado)}
        </h4>
      </div>
    </div>
    
    {!isBalance && (
      <div className="space-y-3">
        <div className="flex justify-between items-end">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Meta: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(previsto)}</span>
          <span className={`text-xs font-black ${color}`}>{pct.toFixed(1)}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(pct, 100)}%` }}
            className={`h-full ${barColor} shadow-lg shadow-current/20`}
          />
        </div>
      </div>
    )}
  </Card>
);

export const InternalFinancePage = ({ orders, users, driverPayments, onAddDriverPayment, addNotification }: any) => {
  const [isDriverLaunchOpen, setIsDriverLaunchOpen] = useState(false);
  const [isNewTxOpen, setIsNewTxOpen] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState<'TODOS' | 'INCOME' | 'EXPENSE'>('TODOS');
  
  const [staffPaymentData, setStaffPaymentData] = useState({
    userId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [newTx, setNewTx] = useState({
    title: '',
    amount: '',
    type: 'INCOME', 
    date: new Date().toISOString().split('T')[0],
    category: 'OUTROS'
  });

  const formatDateBR = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (dateStr.includes('T')) {
        return date.toLocaleDateString('pt-BR');
    } else {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
  };

  const detailedStats = useMemo(() => {
    const recPrevisto = orders.reduce((acc: number, o: Order) => acc + (o.estimatedCost || 0), 0);
    const recRealizado = orders
      .filter((o: Order) => o.status === OrderStatus.CONCLUIDO)
      .reduce((acc: number, o: Order) => acc + (o.estimatedCost || 0), 0);

    let totalEntradasManuais = 0;
    let totalSaidasManuais = 0;

    driverPayments.forEach((p: any) => {
      if (p.type === DriverPaymentType.ADICIONAL || p.type === 'ADICIONAL' || p.type === 'INCOME') {
        totalEntradasManuais += Number(p.amount) || 0;
      } else {
        totalSaidasManuais += Number(p.amount) || 0;
      }
    });
    
    let pagPrevistoExpenses = 0;
    let pagRealizadoExpenses = 0;

    orders.forEach((o: Order) => {
      o.expenses?.forEach(exp => {
        pagPrevistoExpenses += exp.amount;
        if (exp.status === ExpenseStatus.APROVADO) {
          pagRealizadoExpenses += exp.amount;
        }
      });
    });

    const faturamentoTotalRealizado = recRealizado + totalEntradasManuais;
    const faturamentoTotalPrevisto = recPrevisto + totalEntradasManuais;
    const pagTotalPrevisto = totalSaidasManuais + pagPrevistoExpenses;
    const pagTotalRealizado = totalSaidasManuais + pagRealizadoExpenses;

    const saldoPrevisto = faturamentoTotalPrevisto - pagTotalPrevisto;
    const saldoRealizado = faturamentoTotalRealizado - pagTotalRealizado;

    return {
      recebimentos: { previsto: faturamentoTotalPrevisto, realizado: faturamentoTotalRealizado, pct: faturamentoTotalPrevisto > 0 ? (faturamentoTotalRealizado / faturamentoTotalPrevisto) * 100 : 0 },
      pagamentos: { previsto: pagTotalPrevisto, realizado: pagTotalRealizado, pct: pagTotalPrevisto > 0 ? (pagTotalRealizado / pagTotalPrevisto) * 100 : 0 },
      saldo: { previsto: saldoPrevisto, realizado: saldoRealizado }
    };
  }, [orders, driverPayments]);

  const allTransactions = useMemo(() => {
    const list: any[] = [];
    
    orders.forEach((o: any) => {
      list.push({ 
        id: `rev-${o.id}`, 
        type: 'INCOME', 
        title: o.nomeEvento, 
        subtitle: `O.S. ${o.id.split('-')[1] || o.id}`, 
        amount: o.estimatedCost, 
        date: o.createdAt, // ISO string completa
        isRealized: o.status === OrderStatus.CONCLUIDO
      });
    });

    driverPayments.forEach((p: any) => {
       const staff = users.find((u: any) => String(u.id) === String(p.userId));
       const isManual = p.id.startsWith('man-');
       const isIncome = p.type === DriverPaymentType.ADICIONAL || p.type === 'ADICIONAL' || p.type === 'INCOME';
       
       const displayTitle = (p.type === DriverPaymentType.DIARIA && !isManual)
        ? (staff?.name || 'Colaborador') 
        : (p.description || p.type || 'Lançamento Manual');

       const displaySubtitle = isManual 
        ? (isIncome ? 'RECEBIMENTO' : 'PAGAMENTO') 
        : p.type;

       list.push({ 
         id: p.id, 
         type: isIncome ? 'INCOME' : 'EXPENSE', 
         title: displayTitle, 
         subtitle: displaySubtitle, 
         amount: p.amount, 
         date: p.date, // Se for manual apenas data, se for OS tem tempo. Normalizamos no Sort.
         isRealized: true 
       });
    });

    // Ordenação e Filtro
    const filtered = ledgerFilter === 'TODOS' 
        ? list 
        : list.filter(tx => tx.type === ledgerFilter);

    // Garante que o mais novo (maior timestamp) fique em cima
    return filtered.sort((a, b) => {
        const timeA = new Date(a.date).getTime();
        const timeB = new Date(b.date).getTime();
        return timeB - timeA;
    });
  }, [orders, driverPayments, users, ledgerFilter]);

  const handleSaveManualTx = () => {
    if (!newTx.title || !newTx.amount) return;
    const amountNum = parseFloat(newTx.amount.replace(',', '.'));
    
    const mockPayment: DriverPayment = {
      id: `man-${Date.now()}`,
      userId: String(users[0]?.id), 
      type: (newTx.type === 'INCOME' ? DriverPaymentType.ADICIONAL : DriverPaymentType.REEMBOLSO),
      amount: amountNum,
      // Usamos ISO string completa para garantir que o Sort coloque no topo imediatamente
      date: new Date().toISOString(), 
      description: newTx.title,
      status: 'PAID'
    };
    onAddDriverPayment(mockPayment);
    setIsNewTxOpen(false);
    setNewTx({ title: '', amount: '', type: 'INCOME', date: new Date().toISOString().split('T')[0], category: 'OUTROS' });
    addNotification("Sucesso", "Título registrado com sucesso.", "success");
  };

  const handleExecuteDriverPayment = () => {
    if (!staffPaymentData.userId || !staffPaymentData.amount) {
      addNotification("Atenção", "Por favor, selecione o colaborador e insira o valor.", "warning");
      return;
    }
    const amountNum = parseFloat(staffPaymentData.amount);
    const selectedUser = users.find((u: any) => String(u.id) === String(staffPaymentData.userId));
    const paymentRecord: DriverPayment = {
      id: `dp-${Date.now()}`,
      userId: String(staffPaymentData.userId),
      type: DriverPaymentType.DIARIA,
      amount: amountNum,
      date: new Date().toISOString(),
      description: `Diária: ${selectedUser?.name || 'Colaborador'}`,
      status: 'PAID'
    };
    onAddDriverPayment(paymentRecord);
    setIsDriverLaunchOpen(false);
    setStaffPaymentData({ userId: '', amount: '', date: new Date().toISOString().split('T')[0] });
    addNotification("Sucesso", "Pagamento de equipe efetivado com sucesso.", "success");
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-secondary uppercase tracking-tighter italic leading-none">Fluxo de Caixa</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic">Painel de monitoramento Previsto x Realizado.</p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <button onClick={() => setIsDriverLaunchOpen(true)} className="flex-1 md:flex-none bg-emerald-600 text-white px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-700 shadow-xl shadow-emerald-100 transition-all active:scale-95">
            <Users size={18} /> Pagar Equipe
          </button>
          <button onClick={() => setIsNewTxOpen(true)} className="flex-1 md:flex-none bg-primary text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:brightness-110 shadow-xl shadow-primary/20 transition-all">
            <Plus size={18} /> Novo Título
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MonitorCard label="Recebimentos" realizado={detailedStats.recebimentos.realizado} previsto={detailedStats.recebimentos.previsto} pct={detailedStats.recebimentos.pct} icon={ArrowUpRight} color="text-emerald-500" barColor="bg-emerald-500" />
        <MonitorCard label="Pagamentos" realizado={detailedStats.pagamentos.realizado} previsto={detailedStats.pagamentos.previsto} pct={detailedStats.pagamentos.pct} icon={ArrowDownLeft} color="text-red-500" barColor="bg-red-500" />
        <MonitorCard label="Saldo Unidade" realizado={detailedStats.saldo.realizado} previsto={detailedStats.saldo.previsto} pct={detailedStats.saldo.realizado > 0 ? 100 : 0} icon={Wallet} color="text-primary" barColor="bg-primary" isBalance />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <Card className="xl:col-span-2 p-6 md:p-8 bg-white border-none shadow-xl rounded-[32px]">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
             <h3 className="text-xl font-black text-secondary uppercase tracking-tight italic">Extrato Operacional</h3>
             
             {/* FILTRO DE NATUREZA */}
             <div className="bg-gray-50 p-1 rounded-xl flex items-center gap-1 border border-gray-100">
                <button 
                    onClick={() => setLedgerFilter('TODOS')}
                    className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${ledgerFilter === 'TODOS' ? 'bg-white text-secondary shadow-sm' : 'text-gray-400 hover:text-secondary'}`}
                >Todos</button>
                <button 
                    onClick={() => setLedgerFilter('INCOME')}
                    className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${ledgerFilter === 'INCOME' ? 'bg-emerald-500 text-white shadow-md' : 'text-gray-400 hover:text-emerald-600'}`}
                >Entradas</button>
                <button 
                    onClick={() => setLedgerFilter('EXPENSE')}
                    className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${ledgerFilter === 'EXPENSE' ? 'bg-red-500 text-white shadow-md' : 'text-gray-400 hover:text-red-600'}`}
                >Saídas</button>
             </div>
          </div>
          
          <div className="space-y-4">
            {allTransactions.slice(0, 20).map(tx => (
              <div key={tx.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 bg-gray-50/50 rounded-[24px] border border-gray-100 hover:border-primary/20 hover:bg-white transition-all gap-4 animate-in fade-in slide-in-from-top-1 duration-300">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${tx.type === 'INCOME' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    {tx.type === 'INCOME' ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-black text-secondary uppercase text-[12px] md:text-sm truncate">{tx.title}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">{tx.subtitle}</p>
                  </div>
                </div>
                <div className="text-right w-full sm:w-auto">
                  <p className={`font-black text-lg tracking-tighter ${tx.type === 'INCOME' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {tx.type === 'INCOME' ? '+' : '-'} R$ {tx.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[9px] text-gray-400 font-black uppercase flex items-center justify-end gap-1.5 mt-0.5 tracking-widest">
                     <Clock size={10} className="text-primary" /> {formatDateBR(tx.date)}
                  </p>
                </div>
              </div>
            ))}
            {allTransactions.length === 0 && (
                <div className="py-20 text-center opacity-30 italic font-bold uppercase text-[10px] tracking-[0.4em]">Sem lançamentos para este filtro</div>
            )}
          </div>
        </Card>

        <Card className="p-8 bg-[#0f172a] border-none shadow-2xl rounded-[32px] text-white flex flex-col justify-between h-fit relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5 rotate-12 pointer-events-none"><Activity size={150} /></div>
          <div className="relative z-10">
            <h3 className="text-xl font-black uppercase tracking-tight italic mb-8">Saúde do Fluxo</h3>
            <div className="space-y-8">
               <div className="flex flex-col border-b border-white/5 pb-4 last:border-none group">
                  <div className="flex justify-between items-center mb-1">
                     <span className="text-[10px] font-black uppercase text-white/40 tracking-widest">Eficiência de Receita</span>
                     <span className="text-base font-black text-primary">{detailedStats.recebimentos.pct.toFixed(1)}%</span>
                  </div>
                  <p className="text-[9px] text-white/20 font-bold uppercase">Realizado vs Projetado</p>
               </div>
               <div className="flex flex-col border-b border-white/5 pb-4 last:border-none group">
                  <div className="flex justify-between items-center mb-1">
                     <span className="text-[10px] font-black uppercase text-white/40 tracking-widest">Impacto de Saídas</span>
                     <span className="text-base font-black text-primary">{detailedStats.pagamentos.pct.toFixed(1)}%</span>
                  </div>
                  <p className="text-[9px] text-white/20 font-bold uppercase">Consumo de Verba Prevista</p>
               </div>
            </div>
          </div>
        </Card>
      </div>

      {isNewTxOpen && (
        <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
           <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 border-4 border-primary/5">
              <div className="p-8 bg-primary text-white flex justify-between items-center">
                 <h2 className="text-xl font-black uppercase italic leading-none">Novo Título</h2>
                 <button onClick={() => setIsNewTxOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={24} /></button>
              </div>
              <div className="p-10 space-y-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Natureza do Lançamento</label>
                    <div className="grid grid-cols-2 gap-3">
                       <button 
                        type="button"
                        onClick={() => setNewTx({...newTx, type: 'INCOME'})}
                        className={`py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 transition-all flex items-center justify-center gap-2 ${newTx.type === 'INCOME' ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg' : 'bg-gray-50 border-gray-100 text-gray-400 hover:border-emerald-200'}`}
                       >
                          <ArrowUpCircle size={16} /> Entrada (+)
                       </button>
                       <button 
                        type="button"
                        onClick={() => setNewTx({...newTx, type: 'EXPENSE'})}
                        className={`py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 transition-all flex items-center justify-center gap-2 ${newTx.type === 'EXPENSE' ? 'bg-red-500 border-red-500 text-white shadow-lg' : 'bg-gray-50 border-gray-100 text-gray-400 hover:border-red-200'}`}
                       >
                          <ArrowDownCircle size={16} /> Saída (-)
                       </button>
                    </div>
                 </div>

                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Descrição</label>
                    <input className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary" placeholder="Ex: Pagamento de Aluguel" value={newTx.title} onChange={e => setNewTx({...newTx, title: e.target.value})} />
                 </div>
                 
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor (R$)</label>
                       <input type="text" className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-black text-secondary text-xl" placeholder="0,00" value={newTx.amount} onChange={e => setNewTx({...newTx, amount: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data Referência</label>
                       <input type="date" className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={newTx.date} onChange={e => setNewTx({...newTx, date: e.target.value})} />
                    </div>
                 </div>
                 <button onClick={handleSaveManualTx} className="w-full py-5 bg-primary text-white rounded-[24px] font-black uppercase text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3">
                    <Save size={18} /> Registrar Título
                 </button>
              </div>
           </div>
        </div>
      )}

      {isDriverLaunchOpen && (
        <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
           <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden border border-emerald-100 animate-in zoom-in-95">
              <div className="p-8 bg-emerald-600 text-white flex justify-between items-center">
                 <h2 className="text-xl font-black uppercase italic leading-none">Pagamento Equipe</h2>
                 <button onClick={() => setIsDriverLaunchOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={24} /></button>
              </div>
              <div className="p-10 space-y-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Colaborador</label>
                    <select 
                      className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary appearance-none cursor-pointer"
                      value={staffPaymentData.userId}
                      onChange={e => setStaffPaymentData({...staffPaymentData, userId: e.target.value})}
                    >
                       <option value="">Selecionar Colaborador...</option>
                       {users.filter((u:any) => u.role === UserRole.MONTAGEM).map((u:any) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor Diária (R$)</label>
                    <input 
                      type="number" 
                      className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none font-black text-secondary text-3xl shadow-inner" 
                      placeholder="0,00" 
                      value={staffPaymentData.amount}
                      onChange={e => setStaffPaymentData({...staffPaymentData, amount: e.target.value})}
                    />
                 </div>
                 <button 
                  onClick={handleExecuteDriverPayment} 
                  className="w-full py-6 bg-emerald-600 text-white rounded-[28px] font-black uppercase text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3 hover:brightness-110"
                 >
                    <Save size={18} /> Efetivar Lançamento
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};
